/**
 * Inline, contentEditable text field for the workspace top bar (E5 / P2-24).
 *
 * Renders the current value as a single `contentEditable` element — there is no
 * separate "edit mode" and no pencil affordance: the text itself is the field.
 * Click (or tab) to place a caret and type; Enter or blur commits the change
 * (calls `onSave` only when the trimmed value actually changed, is non-empty,
 * and isn't the muted fallback); Escape restores the original. The action is
 * surfaced through the framework styled {@link Tooltip} — never a native
 * `title`. Every colour is a theme token so it reads correctly in light + dark.
 *
 * Layout is selectable via {@link InlineEditableTitleProps.fill}: the default
 * (`false`) takes the text's natural, UNLIMITED width — the project title, never
 * truncated; `true` HUGS its content (sized to the text), capped at the available
 * flex width, and ellipsizes ONLY when the text would overflow — the conversation
 * title, which sits in the gap between the project name and the toolbar icons
 * (the `.mol-conv-title-fill` chain in Workspace.css makes the Tooltip's
 * inline-block trigger participate in that flex sizing so the ellipsis resolves).
 *
 * Shared by the editable project title and the editable conversation title in
 * the workspace header.
 *
 * @module
 */

import {
  type CSSProperties,
  type FocusEvent,
  type JSX,
  type KeyboardEvent,
  useLayoutEffect,
  useRef,
} from 'react'

import { useTranslation } from '@molecule/app-react'
import { Tooltip } from '@molecule/app-ui-react'

/** Props for {@link InlineEditableTitle}. */
export interface InlineEditableTitleProps {
  /** The current (committed) value to display. */
  value: string
  /**
   * Persist a new value. Called only when the trimmed input differs from
   * {@link InlineEditableTitleProps.value}, is non-empty, and isn't the muted
   * fallback. May be async; the field commits optimistically and the caller owns
   * the persisted state.
   */
  onSave: (next: string) => void | Promise<void>
  /** Accessible label for the editable element. */
  ariaLabel: string
  /** Tooltip shown on the field (e.g. "Rename project"). */
  editTooltip: string
  /** Stable automation id, rendered as `data-mol-id` on the editable element. */
  molId: string
  /** Muted text shown when {@link InlineEditableTitleProps.value} is empty. */
  emptyFallback?: string
  /** Maximum characters allowed (clamped as the user types/pastes). */
  maxLength?: number
  /** Text styling for the displayed value (fontSize, fontWeight, color). */
  textStyle?: CSSProperties
  /**
   * Layout mode. `false` (default) → natural, UNLIMITED width: the full text is
   * always shown, never ellipsized (the project title). `true` → HUG the content
   * (sized to the text), capped at the available flex width, ellipsizing only on
   * overflow (the conversation title); pair with the `.mol-conv-title-fill`
   * wrapper so the Tooltip's inline-block trigger flexes (see Workspace.css).
   */
  fill?: boolean
}

/**
 * Renders a single inline contentEditable title with a rename tooltip and full
 * keyboard support (Enter commits, Escape cancels).
 *
 * @param props - Component props.
 * @returns The rendered editable title.
 */
export function InlineEditableTitle({
  value,
  onSave,
  ariaLabel,
  editTooltip,
  molId,
  emptyFallback,
  maxLength = 200,
  textStyle,
  fill = false,
}: InlineEditableTitleProps): JSX.Element {
  const { t } = useTranslation()
  const ref = useRef<HTMLSpanElement>(null)
  // Live focus flag (a ref, not state, so it never triggers a re-render that
  // would re-seed textContent mid-edit and jump the caret).
  const focusedRef = useRef(false)
  // Set by Escape so the ensuing blur restores instead of committing.
  const cancelNextBlurRef = useRef(false)
  // The exact value we last handed to onSave, so a blur immediately after an
  // Enter-commit (or a no-op blur) never fires a duplicate save or stomps the
  // freshly-committed text. Reset whenever `value` changes externally.
  const lastSavedRef = useRef<string | null>(null)

  const display =
    value || emptyFallback || t('workspace.title.untitled', undefined, { defaultValue: 'Untitled' })

  // Seed the element's text from `value` on mount and whenever it changes WHILE
  // NOT focused. Writing textContent mid-edit would reset the caret, so we never
  // touch it while the user is typing; React owns no children here (the text is
  // driven imperatively) so a parent re-render can't clobber an in-progress edit.
  useLayoutEffect(() => {
    // `value` changed externally (incl. our own optimistic rename landing) —
    // allow a future identical edit to save again.
    lastSavedRef.current = null
    if (focusedRef.current) return
    const el = ref.current
    if (el && el.textContent !== display) el.textContent = display
  }, [display])

  const commit = (): void => {
    const el = ref.current
    if (!el) return
    const next = (el.textContent ?? '').trim()
    // Save only a real, changed, non-fallback value we haven't just saved.
    if (next && next !== value && next !== display && next !== lastSavedRef.current) {
      lastSavedRef.current = next
      void onSave(next)
      return
    }
    // Nothing new to save → restore the canonical displayed text (drops empty/
    // whitespace/fallback-only edits), UNLESS we just committed this exact text
    // (then leave it; the optimistic parent update will reconcile it).
    if (next !== lastSavedRef.current && el.textContent !== display) {
      el.textContent = display
    }
  }

  const onKeyDown = (e: KeyboardEvent<HTMLSpanElement>): void => {
    if (e.key === 'Enter') {
      // Commit on Enter (directly, so it doesn't depend on the blur firing),
      // never insert a newline, then blur to leave edit mode.
      e.preventDefault()
      commit()
      ref.current?.blur()
    } else if (e.key === 'Escape') {
      e.preventDefault()
      cancelNextBlurRef.current = true
      if (ref.current) ref.current.textContent = display
      ref.current?.blur()
    }
  }

  // Clamp to maxLength as the user types/pastes, keeping the caret at the end of
  // the clamped text. (contentEditable has no native maxLength.)
  const onInput = (): void => {
    const el = ref.current
    if (!el) return
    const text = el.textContent ?? ''
    if (text.length <= maxLength) return
    el.textContent = text.slice(0, maxLength)
    const sel = typeof window !== 'undefined' ? window.getSelection() : null
    if (sel && el.firstChild) {
      const range = document.createRange()
      range.selectNodeContents(el)
      range.collapse(false)
      sel.removeAllRanges()
      sel.addRange(range)
    }
  }

  const onFocus = (e: FocusEvent<HTMLSpanElement>): void => {
    focusedRef.current = true
    e.currentTarget.style.background = 'var(--color-surface)'
    e.currentTarget.style.borderColor = 'var(--mol-color-primary, #6366f1)'
  }

  const onBlur = (e: FocusEvent<HTMLSpanElement>): void => {
    focusedRef.current = false
    e.currentTarget.style.background = 'transparent'
    e.currentTarget.style.borderColor = 'transparent'
    if (cancelNextBlurRef.current) {
      cancelNextBlurRef.current = false
      // Escape already restored the text; nothing to commit.
      if (e.currentTarget.textContent !== display) e.currentTarget.textContent = display
      return
    }
    commit()
  }

  return (
    <Tooltip content={editTooltip} placement="bottom">
      <span
        ref={ref}
        role="textbox"
        aria-label={ariaLabel}
        data-mol-id={molId}
        tabIndex={0}
        contentEditable
        suppressContentEditableWarning
        spellCheck={false}
        onFocus={onFocus}
        onBlur={onBlur}
        onKeyDown={onKeyDown}
        onInput={onInput}
        onMouseEnter={(e) => {
          if (!focusedRef.current) e.currentTarget.style.background = 'var(--color-surface)'
        }}
        onMouseLeave={(e) => {
          if (!focusedRef.current) e.currentTarget.style.background = 'transparent'
        }}
        style={{
          ...textStyle,
          // Fill mode hugs its content (sized to the text) and ellipsizes ONLY
          // when the text would overflow the available width (conversation
          // title); natural mode hugs its content at unlimited width (project
          // title). `flex: 0 1 auto` = don't grow, shrink when constrained,
          // basis from content; `maxWidth: 100%` caps it at the parent so the
          // ellipsis resolves once it can't fit. (P3-05)
          ...(fill
            ? {
                display: 'block',
                flex: '0 1 auto',
                maxWidth: '100%',
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }
            : { display: 'inline-block' }),
          whiteSpace: 'nowrap',
          padding: '2px 6px',
          borderRadius: 5,
          border: '1px solid transparent',
          outline: 'none',
          background: 'transparent',
          color: textStyle?.color ?? 'var(--color-foreground)',
          cursor: 'text',
          transition: 'background 100ms, border-color 100ms',
        }}
      />
    </Tooltip>
  )
}
